import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { useOrderHistory } from '../../../contexts/OrderHistoryContext';
import { useTheme } from '../../../contexts/ThemeContext';
import MobileMenu from '../MobileMenu/MobileMenu';

import './ActionsMenu.scss';
import Icon from '../Icon/Icon';
import Button from '../Button';

interface ActionsMenuProps {
  onOpenLogin?: () => void;
  onOpenRegister?: () => void;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({ onOpenLogin, onOpenRegister }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  const { cart } = useCart();
  const { hasOrders } = useOrderHistory();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const logoSrc = theme === 'dark' 
    ? '/images/logo/prior-logo-white.png'
    : '/images/logo/prior-logo-black.png';

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Close menu when clicking outside (for desktop actions)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="actions-menu" ref={menuRef}>
      {/* Desktop Actions - Always visible on desktop */}
      <div className="actions-menu__desktop">
        
        {/* Cart Button */}
        {cart && cart.items && cart.items.length > 0 && (
          <button
            className="actions-menu__cart-btn"
            onClick={() => navigate('/cart')}
            aria-label={t('navigation.cart')}
          >
            <Icon name="carrito" size={20} alt={t('navigation.cart')} /> {cart.items.length}
          </button>
        )}
        
        {/* Order History Button */}
        {hasOrders && (
          <button
            className="actions-menu__orders-btn"
            onClick={() => navigate('/order-history')}
            aria-label={t('navigation.orderHistory')}
          >
            <Icon name="pedidos" size={20} alt={t('navigation.orderHistory')} /> {t('navigation.orderHistory')}
          </button>
        )}
        
        {/* Authentication Buttons */}
        {isAuthenticated ? (
          <>
            <Button
              size="sm"
              variant="primary"
              className="actions-menu__dashboard-btn"
              onClick={() => navigate('/dashboard')}
              aria-label={t('navigation.dashboard')}
            >
              <Icon name="panel" size={20} alt={t('navigation.dashboard')} /> {t('navigation.dashboard')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              className="actions-menu__logout-btn"
              onClick={logout}
              aria-label={t('navigation.logout')}
            >
              🚪 {t('navigation.logout')}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="primary"
              className="actions-menu__login-btn"
              onClick={() => (onOpenLogin ? onOpenLogin() : navigate('/login'))}
              aria-label={t('navigation.login')}
            >
              {t('navigation.login')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              className="actions-menu__register-btn"
              onClick={() => (onOpenRegister ? onOpenRegister() : navigate('/register'))}
              aria-label={t('navigation.register')}
            >
              {t('navigation.register')}
            </Button>
          </>
        )}
      </div>

      {/* Mobile Menu */}
      <div className="actions-menu__mobile">
        <MobileMenu 
          logoSrc={logoSrc}
          isActive={isActive}
          variant="authenticated"
        />
      </div>
    </div>
  );
};

export default ActionsMenu; 